// components/FestivalRoadmap.js
"use client";
import React, { useState, useMemo, useEffect } from "react";
import StepCard from "./StepCard";
import ProgressBar from "./ProgressBar";

/**
 * FestivalRoadmap
 * Props:
 *  - steps: array of step objects
 *  - initialActiveId: id of initial active step
 *  - theme: { primary, accent, bg }
 *
 * Notes:
 *  - responsive: md+ horizontal timeline, mobile vertical stacked timeline
 *  - keyboard & screen-reader friendly: aria-expanded, focus-visible
 */

export default function FestivalRoadmap({ steps = [], initialActiveId = null, theme = { primary: "#7c3aed", accent: "#06b6d4", bg: "#010204" } }) {
  const [activeId, setActiveId] = useState(initialActiveId ?? null);
  const [expandedId, setExpandedId] = useState(null);

  // compute percent progress based on activeId
  const percent = useMemo(() => {
    if (!activeId) return 0;
    const idx = steps.findIndex((s) => s.id === activeId);
    if (idx < 0) return 0;
    return Math.round((idx / (steps.length - 1)) * 100);
  }, [activeId, steps]);

  // set default activeId to third step (ثبت‌نام) if not provided
  useEffect(() => {
    if (initialActiveId == null && steps.length > 0) {
      const defaultStep = steps.find((s) => s.id === 3) ?? steps[0];
      setActiveId(defaultStep?.id ?? null);
    } else {
      setActiveId(initialActiveId);
    }
  }, [initialActiveId, steps]);

  const handleToggle = (id) => {
    setExpandedId((cur) => (cur === id ? null : id));
    setActiveId(id);
    // smooth scroll handled inside StepCard
  };

  const handleFocusSelect = (id) => {
    setActiveId(id);
  };

  return (
    <section className="w-full py-12 px-4 md:px-8 lg:px-16" style={{ background: theme.bg }} dir="rtl" aria-label="نقشه راه جشنواره">
      <div className="max-w-7xl mx-auto text-white">
        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl font-extrabold">نقشهٔ راه جشنواره</h2>
          <p className="mt-2 text-base max-w-2xl mx-auto" style={{color: 'rgb(132, 204, 22)'}}>مراحل مهم جشنواره به‌صورت نمودار زمانی</p>
        </div>

        {/* Progress + horizontal timeline for md+ */}
        <div className="hidden md:block">
          <div className="mb-6">
            <ProgressBar percent={percent} theme={{ primary: theme.primary, accent: theme.accent }} height={10} />
          </div>

          <div className="relative">
            <div className="flex items-center justify-between gap-6 flex-wrap">
              {steps.map((s, idx) => {
                const isActive = activeId === s.id;
                const isExpanded = expandedId === s.id;
                return (
                  <div key={s.id} className="flex-1 flex justify-center">
                    <div className="group flex flex-col items-center">
                      <StepCard
                        step={s}
                        isActive={isActive}
                        isExpanded={isExpanded}
                        onToggle={handleToggle}
                        onFocusSelect={handleFocusSelect}
                        theme={{ primary: theme.primary, accent: theme.accent }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile stacked timeline */}
        <div className="md:hidden mt-6 space-y-6">
          {steps.map((s, idx) => {
            const isExpanded = expandedId === s.id;
            return (
              <div key={s.id} className="flex items-start gap-4">
                <div className="flex flex-col items-center mt-1">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})`,
                      boxShadow: `0 0 12px ${theme.primary}33`,
                    }}
                  />
                  {idx !== steps.length - 1 && <div className="w-[2px] grow bg-slate-800/30 mt-2" style={{ minHeight: 48 }} />}
                </div>

                <div className="flex-1">
                  <div className="bg-[#071018] border border-slate-800/30 rounded-2xl p-4 shadow-md">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg" style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}>
                          <div className="w-full h-full flex items-center justify-center text-white font-bold">{s.id}</div>
                        </div>
                        <div className="text-right">
                          <h4 className="text-sm font-bold text-white text-center">{s.title}</h4>
                          {s.date && <div className="text-xs text-slate-400 text-center">{s.date}</div>}
                        </div>
                      </div>

                      <div>
                        <button
                          onClick={() => handleToggle(s.id)}
                          className="px-3 py-2 rounded-md bg-gradient-to-r from-purple-600 to-cyan-400 text-black text-xs font-semibold shadow"
                          aria-expanded={isExpanded}
                          aria-controls={`step-panel-${s.id}`}
                        >
                          {isExpanded ? "بستن" : "جزئیات"}
                        </button>
                      </div>
                    </div>

                    {isExpanded && <div className="mt-3 text-sm text-slate-300 leading-relaxed">{s.details}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-slate-400">شروع ثبت‌نام: <span className="text-slate-100 font-semibold">{steps.find(s => s.id === 3)?.date}</span></div>
          <div className="flex items-center gap-3">
            <a href="/sign-up" className="px-4 py-2 rounded-lg" style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})`, color: "#020617" }}>ثبت‌نام در محور</a>
            <a href="/gallery" className="px-4 py-2 rounded-lg border border-slate-700 text-sm text-slate-200">گالری دوره های پیشین</a>
          </div>
        </div>
      </div>

      
    </section>
  );
}
